import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { FlexBox, Typography, Logo, Grid, SvgIcon, MuiBox } from '@kis-technologies/kis-ui';
import printing from 'assets/lottie/printing';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { LinearProgress } from 'components/Progress';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, PurchaseCartResponse } from 'services/cart';
import { selectors as configSelectors, Configuration, TicketDeliverySettings, Messages } from 'services/configuration';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { actions as maintenanceActions } from 'services/maintenance';
import { selectors as mediaSelectors } from 'services/media';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { formatText, replacePlaceholdersInReceiptTemplate } from 'utils/stringFormat';
import { useStyles } from '../styled';
import { ComponentProps } from '../types';

// Lottie Animation Config
const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: printing,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid meet'
  }
};

export const PrintDelivery = (props: ComponentProps) => {
  const { onCompletion, onErrorConfirmation } = props;
  const { t, i18n } = useTranslation();

  // Action Creators and Selectors
  const [setPrintData, printAllJobs, setLastPrintedOrderDetails, setDeliveryStatus, updatePrintCount] = useActions([
    hardwareActions.setPrintData,
    hardwareActions.printAllJobs,
    maintenanceActions.setLastPrintedOrderDetails,
    ticketingActions.setDeliveryStatus,
    ticketingActions.updatePrintCount
  ]);

  const checkoutCart = useSelector(cartSelectors.selectCheckoutCart);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const printData = useSelector(hardwareSelectors.selectPrintData);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const tickets = useSelector(kioskSelectors.selectTickets);
  const kioskError = useSelector(kioskSelectors.selectError);
  const backgroundImage = useSelector(mediaSelectors.selectBackgroundImage) || [];
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];
  const deliveryStatus = useSelector(ticketingSelectors.selectDeliveryStatus);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const support = get(configuration, `${ticketingPath}.supportName.${i18n.language}`, t('commonText.support'));
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const fonts = get(configuration, 'configuration.globalSettings.fonts');
  const flowTemplate = get(configuration, 'configuration.flowTemplate') as string;
  const souvenirTicketEnabled = get(configuration, `${ticketingPath}.souvenirTicket.enabled`, false);
  const printDeliveryConfig: TicketDeliverySettings['print'] = get(
    configuration,
    `${ticketingPath}.ticketDeliverySettings.print`,
    {}
  );
  const receiptPromptEnabled: boolean = get(printDeliveryConfig, 'receiptPrompt.enabled', false);
  const receiptPromptTimeout: number = get(printDeliveryConfig, 'receiptPrompt.inactivityTimeoutDuration', 0);
  const receiptPromptMessages: Messages = get(printDeliveryConfig, 'receiptPrompt.messages', {});
  const receiptPromptHeaderText: string = get(
    receiptPromptMessages,
    `header.${i18n.language}`,
    t('delivery.receiptPromptHeader')
  );
  const receiptPromptCancelText: string = get(receiptPromptMessages, `buttons.${i18n.language}[0]`, t('commonText.no'));
  const receiptPromptConfirmText: string = get(
    receiptPromptMessages,
    `buttons.${i18n.language}[1]`,
    t('commonText.yes')
  );
  const ticketsMessages: Messages = get(printDeliveryConfig, `deliveryInProgress.ticketsMessages`, {});
  const ticketsHeaderText: string = get(
    ticketsMessages,
    `header.${i18n.language}`,
    `${t('delivery.printing')} ${t('delivery.ticket')} \n {{nTicket}} ${t('delivery.of')} {{totalTickets}}`
  );
  const ticketsSubheaderText: string = get(
    ticketsMessages,
    `subheader.${i18n.language}`,
    t('delivery.lookBelowForTicket')
  );
  const receiptsMessages: Messages = get(printDeliveryConfig, `deliveryInProgress.receiptsMessages`, {});
  const receiptsHeaderText: string = get(
    receiptsMessages,
    `header.${i18n.language}`,
    `${t('delivery.printing')} {{receiptLabel}}`
  );
  const receiptsSubheaderText: string = get(
    receiptsMessages,
    `subheader.${i18n.language}`,
    t('delivery.lookBelowForTicket')
  );
  const errorOverlayMessages: Messages = get(printDeliveryConfig, `errorOverlayMessages`, {});
  const errorOverlayHeaderText: string = get(
    errorOverlayMessages,
    `header.${i18n.language}`,
    t('saleError.printErrorHeader')
  );
  const errorOverlaySubheaderText: string = get(errorOverlayMessages, `subheader.${i18n.language}`, '');
  const errorOverlayConfirmText: string = get(errorOverlayMessages, `buttons.${i18n.language}[0]`, t('commonText.ok'));
  const clientId = get(kiosk, 'venue.clientId');
  const isPrinting = printData.loading;

  const [isReceiptPrinting, setIsReceiptPrinting] = useState<boolean>(false);
  const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(false);
  const [showReceiptPrompt, setShowReceiptPrompt] = useState<boolean>(false);

  const classes = useStyles();

  const bgImageName = get(backgroundImage[0], 's3Name') || 'default.png';
  const imageBaseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}`;
  const backgroundUrl = `${imageBaseUrl}/background_images/${bgImageName}`;

  // Set the tickets as printJobs and print them
  useEffect(() => {
    if (tickets.length && !printData.printJobs.length) {
      setLastPrintedOrderDetails({
        dateTime: new Date().toISOString(),
        printJobs: tickets
      });

      const ticketsOnly = tickets.filter((ticket) => ticket.ticketNumber !== 'receipt');
      if (ticketsOnly.length) {
        setPrintData({ printJobs: ticketsOnly });
        printAllJobs();
      } else {
        handleReceiptsOrContinue();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  useEffect(() => {
    if (flowTemplate === 'fair' && deliveryStatus && !isReceiptPrinting) {
      // Fair flow: update the print count after ticket delivery (completed or failed)
      updatePrintCount({
        ticketNumber: get(printData, 'printJobs[0].ticketNumber', ''),
        printCount: printData.printJobs.filter((job) => job.printed).length,
        totalCount: printData.total
      });
    }

    if (deliveryStatus === 'completed' && !showErrorOverlay) {
      handleReceiptsOrContinue();
    } else if (deliveryStatus === 'failed') {
      // Fair flow: print the receipt if there was an error printing coupons
      if (flowTemplate === 'fair') initiateReceiptPrinting();
      setShowErrorOverlay(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStatus]);

  // Set a timeout if the receipt prompt is active
  useEffect(() => {
    if (showReceiptPrompt && receiptPromptTimeout) {
      const timer = setTimeout(() => {
        onReceiptPromptCancel();
      }, receiptPromptTimeout * 1000); // convert to milliseconds

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [showReceiptPrompt]);

  useEffect(() => {
    if (kioskError) {
      // Souvenir ticket flow: ignore error and continue to completion
      if (souvenirTicketEnabled) onCompletion();
      else setShowErrorOverlay(true);
    }
  }, [kioskError]);

  /**
   * Continue forward if there are no receipts.
   * If there are receipt(s), then either start printing them or
   * show the prompt if that configuration is enabled.
   */
  const handleReceiptsOrContinue = () => {
    const receipts = tickets.filter((ticket) => ticket.ticketNumber === 'receipt');
    if (!receipts.length || isReceiptPrinting) {
      onCompletion();
    } else if (receiptPromptEnabled) {
      setShowReceiptPrompt(true);
    } else {
      initiateReceiptPrinting();
    }
  };

  /**
   * Set the receipts to be printed, and start the printing process.
   */
  const initiateReceiptPrinting = () => {
    let receipts = tickets.filter((ticket) => ticket.ticketNumber === 'receipt');

    // Fair flow: replace the macros for the receipt template
    if (flowTemplate === 'fair') {
      receipts = receipts.map((receipt) => ({
        ...receipt,
        message: replacePlaceholdersInReceiptTemplate(
          receipt.message,
          checkoutCart as PurchaseCartResponse,
          kiosk as Kiosk,
          printData
        )
      }));
    }

    setPrintData({ printJobs: receipts });
    setDeliveryStatus('');
    setIsReceiptPrinting(true);
    printAllJobs();
  };

  const onReceiptPromptCancel = () => {
    onCompletion();
  };

  const onReceiptPromptConfirm = () => {
    initiateReceiptPrinting();
    // Fair flow: continue to the next page without showing "Printing receipt"
    if (flowTemplate === 'fair') {
      onCompletion();
    } else {
      setShowReceiptPrompt(false);
    }
  };

  /**
   * Returns text to use as the header for the printing progress, which can be
   * different for tickets and receipts. The configurations for both allow for macros:
   * - tickets: {{nTicket}} and {{totalTickets}}
   * - receipts: {{receiptLabel}}
   */
  const getPrintingHeaderText = (): string => {
    let printingHeaderText = '';

    if (isReceiptPrinting) {
      const countOfReceipts = printData.printJobs.filter((job) => job.ticketNumber === 'receipt').length;
      const receiptLabel = countOfReceipts > 1 ? t('delivery.receipt_plural') : t('delivery.receipt');
      printingHeaderText = receiptsHeaderText.replace('{{receiptLabel}}', receiptLabel);
    } else {
      const countOfTickets = printData.printJobs.filter((job) => job.ticketNumber !== 'receipt').length;
      printingHeaderText = ticketsHeaderText
        .replace('{{nTicket}}', ((printData.count as number) + 1).toString())
        .replace('{{totalTickets}}', countOfTickets.toString());
    }

    return printingHeaderText;
  };

  /**
   * Returns text to use as the subheader for the error overlay.
   * The configuration allows for one macro: {{orderId}}. If not configured,
   * there are two default strings depending on if the orderId is known.
   */
  const getErrorOverlaySubheader = (): string => {
    let errorMessage: string;
    const orderId = (checkoutCart && checkoutCart.orderId) || '';

    if (errorOverlaySubheaderText) {
      errorMessage = errorOverlaySubheaderText.replace('{{orderId}}', orderId);
    } else if (orderId) {
      errorMessage = t('saleError.printErrorMsg', { orderId: `#${orderId}`, support });
    } else {
      errorMessage = t('saleError.notifySupport', { support });
    }

    return errorMessage;
  };

  const backgroundStyle = () => {
    if (palette && palette.darkPrimary) {
      return { background: `${palette.darkPrimary}e5` };
    }
    return { background: 'rgba(255,255,255,0.95)' };
  };

  return (
    <>
      {!(tickets && tickets.length) && !isPrinting && !showErrorOverlay && <LoadingOverlay />}
      {flowTemplate !== 'fair' && tickets && tickets.length > 0 && (
        <Grid className={classes.deliveryOuterContainer}>
          <Grid container className={classes.deliveryInnerContainer}>
            {showReceiptPrompt && (
              <ActionOverlay
                title={receiptPromptHeaderText}
                confirmText={receiptPromptConfirmText}
                cancelText={receiptPromptCancelText}
                height={500}
                onCancel={onReceiptPromptCancel}
                onConfirm={onReceiptPromptConfirm}
              />
            )}
            <Grid item lg={12} xl={6} className={classes.deliveryTextContainer}>
              {/* Is isPrinting is true, show printing content - otherwise, re-route to CompletionView */}
              {isPrinting && (
                <MuiBox className={classes.deliveryPrintingText}>
                  <Grid className={classes.deliveryPrintingHeader}>
                    <Typography
                      fontSize={100}
                      fontWeight={600}
                      sx={{ lineHeight: 1.1, whiteSpace: 'pre-wrap' }}
                      align="center"
                    >
                      {/* Leave a space between the text and Icon */}
                      {`${getPrintingHeaderText()} `}
                      <SvgIcon name="down_circle" size={70} />
                    </Typography>
                  </Grid>
                  <Typography fontSize={30}>
                    {isReceiptPrinting ? receiptsSubheaderText : ticketsSubheaderText}
                  </Typography>
                </MuiBox>
              )}
            </Grid>
            <Grid item lg={12} xl={6} className={classes.deliveryImageContainer}>
              <FlexBox fullHeight padding="0 20" align="center" justify="center">
                <Lottie options={lottieOptions} isClickToPauseDisabled />
              </FlexBox>
            </Grid>
          </Grid>
        </Grid>
      )}
      {flowTemplate === 'fair' && tickets && tickets.length > 0 && (
        <FlexBox height="1920px" overflow="hidden">
          {showReceiptPrompt && (
            <ActionOverlay
              style={{ zIndex: '98', background: `${palette?.darkPrimary}e5` }}
              title={
                <Typography fontSize={45} align="center" color={palette?.primaryText}>
                  {formatText(receiptPromptHeaderText, flowTemplate)}
                </Typography>
              }
              confirmText={formatText(receiptPromptConfirmText, flowTemplate)}
              cancelText={formatText(receiptPromptCancelText, flowTemplate)}
              height={300}
              flow={flowTemplate}
              onCancel={onReceiptPromptCancel}
              onConfirm={onReceiptPromptConfirm}
            />
          )}
          <FlexBox fullWidth maxHeight="1920px" sx={{ backgroundImage: `url(${backgroundUrl})` }}>
            <FlexBox height="12.5%" fullWidth align="end" justify="center">
              {logoImage && logoImage.length > 0 ? (
                <img alt="url" src={`${imageBaseUrl}/logo_images/${logoImage[0].s3Name}`} style={{ height: 97 }} />
              ) : (
                <Logo />
              )}
            </FlexBox>
            <FlexBox height="15%" fullWidth align="center" justify="center">
              <Typography
                color={palette?.primaryText}
                sx={{ lineHeight: '1', fontFamily: fonts?.secondary, margin: '0 20px' }}
                fontSize={125}
                align="center"
              >
                PAYMENT APPROVED!
                <br />
                PRINTING COUPONS
              </Typography>
            </FlexBox>
            <FlexBox height="5%" fullWidth align="end" justify="center">
              <Typography
                color={palette?.primaryText}
                fontSize={42}
                sx={{ fontFamily: fonts?.primary, margin: '5px' }}
                align="center"
              >
                YOUR COUPONS WILL
                <br />
                DISTRIBUTE BELOW
              </Typography>
            </FlexBox>
            {isPrinting && (
              <>
                <FlexBox direction="column" fullWidth height="20%" align="center" justify="center">
                  <LinearProgress
                    variant="determinate"
                    value={(((printData.count as number) + 1) / (printData.total as number)) * 100}
                  />
                  <FlexBox fullWidth align="center" justify="center">
                    <Typography
                      color={palette?.primaryText}
                      fontSize={42}
                      sx={{ fontFamily: fonts?.primary, margin: '50px 5px 0px' }}
                      align="center"
                    >
                      COUPON SHEET
                    </Typography>
                  </FlexBox>
                  <FlexBox fullWidth align="start" justify="center">
                    <Typography
                      color={palette?.primaryText}
                      fontSize={64}
                      sx={{ fontFamily: fonts?.primary, margin: '5px' }}
                      align="center"
                    >
                      {(printData.count as number) + 1} OF {printData.total}
                    </Typography>
                  </FlexBox>
                </FlexBox>
                <FlexBox fullWidth height="20%" align="center" justify="center">
                  <Lottie options={lottieOptions} isClickToPauseDisabled />
                </FlexBox>
                <FlexBox fullWidth height="18%">
                  <Typography
                    color={palette?.primaryText}
                    fontSize={42}
                    sx={{ fontFamily: fonts?.primary, margin: '100px' }}
                    align="center"
                  >
                    THANKS!
                    <br />
                    YOU MAY NOW REMOVE YOUR CREDIT CARD.
                  </Typography>
                </FlexBox>
              </>
            )}
          </FlexBox>
        </FlexBox>
      )}

      {showErrorOverlay && (
        <ActionOverlay
          title={errorOverlayHeaderText}
          description={
            <Typography align="center" fontSize={30}>
              {getErrorOverlaySubheader()}
            </Typography>
          }
          confirmText={errorOverlayConfirmText}
          onConfirm={onErrorConfirmation}
          height={500}
          style={backgroundStyle()}
          flow={flowTemplate}
        />
      )}
    </>
  );
};
